import { useCallback, useReducer } from 'react';
import { AsyncStatus } from '@algoan/dashboard/model';

interface ResponseError extends Error {
  httpStatus?: number;
}
type ErrorMessage = 'notFound' | 'genericError' | undefined;
type ActionType = { type: 'START' } | { type: 'SUCCESS' } | { type: 'ERROR'; error: ResponseError };
type UseAsyncType = {
  asyncStatus: AsyncStatus;
  asyncError: ErrorMessage;
};
export type RunType<T> = (promise: Promise<T>) => Promise<void | T>;

const initialState: UseAsyncType = {
  asyncStatus: AsyncStatus.IDLE,
  asyncError: undefined,
};

const asyncReducer = (state: UseAsyncType, action: ActionType): UseAsyncType => {
  switch (action.type) {
    case 'START': {
      return { ...state, asyncStatus: AsyncStatus.PENDING };
    }
    case 'SUCCESS': {
      return { ...state, asyncStatus: AsyncStatus.RESOLVED };
    }
    case 'ERROR': {
      const errorMessage = action.error?.httpStatus === 404 ? 'notFound' : 'genericError';

      return { ...state, asyncStatus: AsyncStatus.REJECTED, asyncError: errorMessage };
    }
  }
};

export const useAsync = <T>(): {
  asyncStatus: AsyncStatus;
  asyncError: ErrorMessage;
  run: RunType<T>;
} => {
  const [{ asyncStatus, asyncError }, dispatch] = useReducer(asyncReducer, initialState);

  const run = useCallback(
    (promise: Promise<T>) => {
      dispatch({ type: 'START' });
      return promise
        .then((data: T) => {
          dispatch({ type: 'SUCCESS' });
          return data;
        })
        .catch((error: ResponseError) => dispatch({ type: 'ERROR', error }));
    },
    [dispatch],
  );

  return {
    asyncStatus,
    asyncError,
    run,
  };
};
